import React, { useMemo, useState } from "react";
import ValidatorTextField from "../../Validator/ValidatorTextField";
import { translate } from "../../../services/Translations/translatorService";
import { formValue, transformToFormValues } from "../../../services/formServiceFunctions";
import ValidatorSelect from "../../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import ValidatorDateField from "../../Validator/ValidatorDatePicker";
import _ from "lodash";
import InsurancePicker from "../../InsurancePicker/InsurancePicker";
import ContractLawRepresentatives from "../../ContractLawRepresentatives/ContractLawRepresentatives";
import CaseRejectionCategorySelect from "../../CaseRejectionCategorySelect/CaseRejectionCategorySelect";
import ProcessParticipantsPeopleForm from "./Elements/ProcessParticipantsPeopleForm";
import ProcessParticipantsOrganizationsForm from "./Elements/ProcessParticipantsOrganizationsForm";
import FormElementAlert from "./FormElementAlert";
import CourtPicker from "../../CourtPicker/CourtPicker";
import CaseEntityPicker from "../CaseEntityPicker/CaseEntityPicker";
import BeaLawyerImportButton from "./Elements/BeaLawyerImportButton";
import ExpandableTextField from "../../Validator/ExpandableTextField";
import ValidatorNumberField, { ValidatorNumberFieldProps } from "../../Validator/ValidatorNumberField";
import AdditionalClientsForm from "./Elements/AdditionalClientsForm";
import { ValidatorType } from "../../../types/ValidatorType";
import { UseForm } from "../../../types/UseForm";
import { SelectOption } from "../../../types/SelectOption";
import SelectAndExtendedDataComboField from "../../SelectAndExtendedDataComboField/SelectAndExtendedDataComboField";
import ValidatorOrganizationAutocomplete from "../../Validator/ValidatorOrganizationAutocomplete";
import AlimonyExpenses from "../../AlimoneyExpenses/AlimonyExpenses";
import AddProcessParticipantForm from "./Elements/AddProcessParticipantForm";
import { ProductUnion } from "../../../types/ProductUnion";
import { OrganizationHintsForm } from "../../OrganizationHints/OrganizationHintsForm";
import ValidatorAutoComplete from "../../Validator/ValidatorAutoComplete";
import ValidatorGooglePlacesAutocomplete from "../../Validator/ValidatorGooglePlacesAutocomplete";
import AppendableListField from "../../AppendableListField/AppendableListField";

export type FormElementProps<T extends ProductUnion = ProductUnion> = {
  type:
    | "ValidatorTextField"
    | "ValidatorSelect"
    | "ValidatorOrganizationAutocomplete"
    | "ValidatorDateField"
    | "InsurancePicker"
    | "DebtCollectionAgencyPicker"
    | "ContractualPartnerLegalEntityPicker"
    | "AuthorityPicker"
    | "ProsecutionPicker"
    | "OpposingLiabilityInsurancePicker"
    | "CourtPicker"
    | "ContractRepresentatives"
    | "CaseRejectionCategorySelect"
    | "AddProcessParticipantForm"
    | "ProcessParticipantsPeopleForm"
    | "ProcessParticipantsOrganizationsForm"
    | "EmptyElement"
    | "BeaLawyerImportButton"
    | "ExpandableTextField"
    | "ValidatorNumberField"
    | "SelectAndExtendedDataComboField"
    | "AdditionalClientsForm"
    | "AlimonyExpenses"
    | "OrganizationHintsForm"
    | "ValidatorAutoComplete"
    | "ValidatorGooglePlacesAutocomplete"
    | "AppendableListField";
  path: string;
  translationPath?: string | (({ product, values }: { product?: T; values: UseForm["values"] }) => string);
  helperText?: string;
  isDisabled?: ({ product, values }: { product: T; values: UseForm["values"] }) => boolean;
  options?: ({ product, values }: { product: T; values: UseForm["values"] }) => SelectOption[];
  validators?: Array<ValidatorType>;
  isHidden?: ({ product, values }: { product: T; values: UseForm["values"] }) => boolean;
  additionalProps?: Record<string, any>;
  label?: string;
  mandatoryFields?: Array<string>;
  product: T;
  alert?: Record<string, any>;
  refreshPage?: Function;
  numberField?: "float" | "int";
} & Partial<Pick<ValidatorNumberFieldProps, "fieldType" | "numberType" | "dependentValidationFields">>;
export type FormElementPropsWithoutProduct<T extends ProductUnion = ProductUnion> = Omit<
  FormElementProps<T>,
  "product"
>;

const FormElement = ({
  type,
  path,
  label,
  isDisabled = () => {
    return false;
  },
  isLoading,
  helperText,
  options = () => {
    return [];
  },
  validators,
  isHidden = () => {
    return false;
  },
  additionalProps = {},
  values,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  registerValidators,
  handleDateChange,
  mandatoryFields = [],
  product,
  alert,
  translationPath,
  refreshPage = () => {},
  fieldType,
  numberType,
  dependentValidationFields,
}: FormElementProps & UseForm) => {
  const [isShowAlert, setIsShowAlert] = useState(false);
  const initialValues = useMemo(() => {
    return { [product?.productClassName || "base"]: transformToFormValues(product) };
  }, [product]);

  if (isHidden({ values, product })) {
    return null;
  }

  const handleBlurInternal: UseForm["handleBlur"] = (e) => {
    if (!alert) {
      handleBlur(e);
      return;
    }
    setIsShowAlert(true);
  };

  const getOptionLabel = (option: SelectOption, path: string) => {
    if (_.isString(option.label)) {
      return option.label;
    }

    if (option.value === "__null__") {
      return " ";
    }

    return translate(`${path}.values.${option.value}`);
  };

  if (_.isFunction(translationPath)) {
    translationPath = translationPath({ values });
  }

  const internalTranslationPath = translationPath || path;
  let formField;
  let initialValue = formValue(initialValues, path);
  switch (type) {
    case "ValidatorTextField":
      formField = (
        <ValidatorTextField
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path)}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          autoComplete={"none"}
          onChange={handleChange}
          onBlur={handleBlurInternal}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          initialDependentValidationFields={[]}
          {...additionalProps}
        />
      );
      break;
    case "ValidatorNumberField":
      formField = (
        <ValidatorNumberField
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path)}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          autoComplete={"none"}
          onChange={handleChange}
          onBlur={handleBlurInternal}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          fieldType={fieldType}
          numberType={numberType}
          {...additionalProps}
        />
      );
      break;
    case "ExpandableTextField":
      formField = (
        <ExpandableTextField
          dependentValidationFields={[]}
          maxRows={undefined}
          buttonLabel={undefined}
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path)}
          disabled={isLoading || isDisabled({ values, product })}
          isMandatory={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          autoComplete={"none"}
          onChange={handleChange}
          onBlur={handleBlurInternal}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          {...additionalProps}
        />
      );
      break;
    case "ValidatorSelect":
      formField = (
        <ValidatorSelect
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path)}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          onChange={handleChange}
          onBlur={handleBlurInternal}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          dependentValidationFields={dependentValidationFields}
          {...additionalProps}
        >
          {_.map(options({ values, product }), (option, index) => {
            return (
              // @ts-ignore ignored because we also have booleans in values
              <MenuItem key={index} value={option.value} disabled={!!option.disabled}>
                {getOptionLabel(option, internalTranslationPath)}
              </MenuItem>
            );
          })}
        </ValidatorSelect>
      );
      break;
    case "ValidatorOrganizationAutocomplete":
      formField = (
        <ValidatorOrganizationAutocomplete
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path)}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          onChange={handleChange}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          {...additionalProps}
        />
      );
      break;
    case "ValidatorAutoComplete":
      formField = (
        <ValidatorAutoComplete
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path) || null}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          onChange={handleChange}
          onInputChange={(e) => {
            if (e && "value" in e.target) {
              handleChange({
                target: {
                  name: path,
                  value: e.target.value,
                },
              });
            }
          }}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          extractNewValue={additionalProps.extractNewValue}
          getSelectedValue={additionalProps.getSelectedValue}
          getOptionLabel={additionalProps.getOptionLabel}
          {...additionalProps}
          options={additionalProps.options({ values, product })}
        />
      );
      break;
    case "AppendableListField":
      formField = (
        <AppendableListField
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          onChange={handleChange}
          value={formValue(values, path) || []}
          helperText={errors[path] || helperText}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          initialDependentValidationFields={[]}
          valueKey={additionalProps.valueKey ?? null}
          inputValidator={additionalProps.inputValidator ?? null}
        />
      );
      break;
    case "CaseRejectionCategorySelect":
      formField = (
        <CaseRejectionCategorySelect
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path)}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          onChange={handleChange}
          onBlur={handleBlurInternal}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          {...additionalProps}
        >
          {_.map(options({ values, product }), (option, index) => {
            return (
              // @ts-ignore ignored because we also have booleans in values
              <MenuItem key={index} value={option.value} disabled={!!option.disabled}>
                {getOptionLabel(option, internalTranslationPath)}
              </MenuItem>
            );
          })}
        </CaseRejectionCategorySelect>
      );
      break;
    case "ValidatorDateField":
      initialValue = formValue(initialValues, path, undefined);
      formField = (
        <ValidatorDateField
          label={translate(`${internalTranslationPath}.label`, label)}
          name={path}
          value={formValue(values, path, undefined)}
          disabled={isLoading || isDisabled({ values, product })}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          onChange={(date) => handleDateChange(date, path)}
          handleBlur={handleBlurInternal}
          registerValidators={registerValidators}
          validators={validators}
          error={!!errors[path]}
          helperText={errors[path] || helperText}
          {...additionalProps}
        />
      );
      break;
    case "InsurancePicker":
      formField = (
        <InsurancePicker
          label={translate(`${internalTranslationPath}.label`)}
          name={`${path}.id`}
          value={formValue(values, `${path}.id`)}
          disabled={isLoading || isDisabled({ values, product })}
          isMandatory={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          onChange={handleChange}
          registerValidators={registerValidators}
          validators={validators}
          dependentValidationFields={[]}
          {...additionalProps}
        />
      );
      break;
    case "ContractualPartnerLegalEntityPicker":
      formField = (
        <CaseEntityPicker
          path={_.replace(path, product?.productClassName + ".", "")}
          type={"contractualPartnerLegalEntity"}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "DebtCollectionAgencyPicker":
      formField = (
        <CaseEntityPicker
          path={_.replace(path, product?.productClassName + ".", "")}
          type={"debtCollectionAgency"}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "AuthorityPicker":
      formField = (
        <CaseEntityPicker
          path={_.replace(path, product?.productClassName + ".", "")}
          type={"authority"}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "ProsecutionPicker":
      formField = (
        <CaseEntityPicker
          path={_.replace(path, product?.productClassName + ".", "")}
          type={"prosecution"}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "CourtPicker":
      formField = (
        <CourtPicker disabled={isLoading || isDisabled({ values, product })} courtTypes={additionalProps.courtTypes} />
      );
      break;
    case "OpposingLiabilityInsurancePicker":
      formField = (
        <CaseEntityPicker
          path={_.replace(path, product?.productClassName + ".", "")}
          type={"opposingLiabilityInsurance"}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "ContractRepresentatives":
      formField = (
        <ContractLawRepresentatives
          name={path}
          values={formValue(values, path, undefined)}
          disabled={isLoading || isDisabled({ values, product })}
          onChange={handleChange}
          {...additionalProps}
        />
      );
      break;
    case "SelectAndExtendedDataComboField":
      formField = (
        <SelectAndExtendedDataComboField
          config={additionalProps.config}
          path={path}
          formValue={JSON.parse(formValue(values, path))}
          onChange={handleChange}
          isHighlighted={mandatoryFields.includes(_.replace(path, product?.productClassName + ".", ""))}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "AddProcessParticipantForm":
      formField = <AddProcessParticipantForm product={product} refreshPage={refreshPage} />;
      break;
    case "ProcessParticipantsPeopleForm":
      formField = <ProcessParticipantsPeopleForm product={product} refreshPage={refreshPage} />;
      break;
    case "ProcessParticipantsOrganizationsForm":
      formField = <ProcessParticipantsOrganizationsForm product={product} refreshPage={refreshPage} />;
      break;
    case "BeaLawyerImportButton":
      formField = <BeaLawyerImportButton product={product} handleChange={handleChange} values={values} />;
      break;
    case "AdditionalClientsForm":
      formField = <AdditionalClientsForm product={product} refreshPage={refreshPage} />;
      break;
    case "AlimonyExpenses":
      formField = (
        <AlimonyExpenses
          path={path}
          formValue={formValue(values, path)}
          onChange={handleChange}
          disabled={isLoading || isDisabled({ values, product })}
        />
      );
      break;
    case "OrganizationHintsForm":
      formField = (
        <OrganizationHintsForm
          disabled={isDisabled({ values, product })}
          /*
           * Needs to replace "." on the end of path which is set in "backoffice-client/src/services/Product/FormDefinitions/modules/organizationFields.tsx"
           * because this component renders its own input fields. And it can be rendered in /all-daten page which also does not set "." at the end.
           * This fixes to get the correct values from formValue function which dont works with "." at the end
           */
          values={path ? formValue(values, path.replace(/\.$/, "")) : values}
          translationPath={internalTranslationPath}
          path={path}
          handleChange={handleChange}
          registerValidators={registerValidators}
          handleBlur={handleBlur}
          handleDateChange={handleDateChange}
          errors={errors}
        />
      );
      break;
    case "ValidatorGooglePlacesAutocomplete":
      formField = (
        <ValidatorGooglePlacesAutocomplete
          path={path}
          translationPath={translationPath}
          errors={errors}
          values={values}
          handleBlur={handleBlur}
          handleChange={handleChange}
          registerValidators={registerValidators}
          validators={validators}
          disabled={isLoading || isDisabled({ values, product })}
          postalCodePath={additionalProps.postalCodePath}
          addressLocalityPath={additionalProps.addressLocalityPath}
        />
      );
      break;
    default:
      return null;
  }

  return (
    <>
      {formField}
      {!!alert && (
        <FormElementAlert
          alert={alert}
          label={translate(`${internalTranslationPath}.label`, label)}
          initialValue={initialValue}
          currentValue={formValue(values, path)}
          path={path}
          handleChange={handleChange}
          onConfirm={handleSubmit}
          isShowAlert={isShowAlert}
          setIsShowAlert={setIsShowAlert}
        />
      )}
    </>
  );
};

export default FormElement;
